import { type IConfigStore } from "../common/configstore";
import { type Session } from "../common/types";
import { uuid } from "../common/utils";

export interface SessionCallback {
  onNewSession: (_session: Session) => void;
  onContinueSession?: (
    _session: Session,
    _activeTime: number,
    _sessionLength: number
    //_content: string[]
  ) => void;
  onEndSession: (_session: Session) => void;
  /**
   * Called after the final engagement heartbeat is recorded because the page
   * was hidden or is being unloaded. Pending events should be sent now with a
   * transport that survives the page going away.
   */
  onPageHidden?: () => void;
}

interface Settings {
  // must be smaller than idle timeout.
  idleTimeCheckIntervalMs?: number;

  // must be smaller than session timeout.
  idleTimeoutMs?: number;

  // must be large. session is tracked across tabs.
  sessionTimeoutMs?: number;

  callback: SessionCallback;
  configStore: IConfigStore;
}
interface Times {
  start: number;
  stop: number | null;
}

const windowIdleEvents = ["scroll", "resize"];
const documentIdleEvents = [
  "keydown",
  "keyup",
  "mousedown",
  "mousemove",
  "touchstart",
  "touchmove",
  "click",
  "contextmenu",
];

const SESSION_KEY = "session";

interface SessionInternal extends Session {
  lastActivityTime: number;
}

const modifyEventListeners = (
  action: "add" | "remove",
  target: Window | Document,
  events: string[],
  listener: EventListenerOrEventListenerObject,
  options?: boolean | AddEventListenerOptions
): void => {
  events.forEach((event) => {
    target[`${action}EventListener`](event, listener, options);
  });
};

const documentListenerOptions = { passive: true };
const windowListenerOptions = { ...documentListenerOptions, capture: true };
export class InteractionTime {
  private times: Times[];
  private isIdle: boolean;
  // Active time last sent in a heartbeat; used to skip duplicate final reports.
  private lastNotifiedActiveTime = -1;

  private idleIntervalId?: number;
  private readonly idleTimeoutMs: number;
  private readonly sessionTimeoutMs: number;
  private readonly idleTimeCheckIntervalMs: number;
  private readonly callback: SessionCallback;
  private readonly configStore: IConfigStore;

  private session!: SessionInternal;

  constructor({
    configStore,
    callback,
    idleTimeCheckIntervalMs = 10 * 1000,
    idleTimeoutMs = 30 * 1000,
    sessionTimeoutMs = 10 * 60 * 1000,
  }: Settings) {
    this.idleTimeCheckIntervalMs = idleTimeCheckIntervalMs;
    this.idleTimeoutMs = idleTimeoutMs;
    this.sessionTimeoutMs = sessionTimeoutMs;
    this.callback = callback;
    this.configStore = configStore;
    const existingSession = this.getStoredSession();
    if (existingSession != null) {
      this.session = existingSession;
    }

    this.times = [];
    this.isIdle = true;
    this.registerEventListeners();
    this.onActivity(); // to start the session.
  }

  /**
   * Checks if a session needs to be started or resumed.
   * starts if this is the first time user is visiting.
   * Uses Date.now() for cross-page session tracking consistency.
   * @returns
   */
  private readonly refreshSession = (session?: SessionInternal): void => {
    if (session != null) {
      const elapsed = Date.now() - session.lastActivityTime;
      if (elapsed >= 0 && elapsed < this.sessionTimeoutMs) {
        this.session = session;
      } else {
        this.callback.onEndSession(session);
        this.createNewSession();
      }
    } else {
      this.createNewSession();
    }
  };

  private readonly getStoredSession = (): SessionInternal | undefined => {
    return this.configStore.get(SESSION_KEY) as SessionInternal | undefined;
  };

  private readonly syncSessionFromStore = (): void => {
    const storedSession = this.getStoredSession();
    if (storedSession == null) return;

    if (this.session == null) {
      this.session = storedSession;
      return;
    }

    // Keep the freshest session copy across tabs.
    if (
      storedSession.id !== this.session.id ||
      storedSession.lastActivityTime > this.session.lastActivityTime
    ) {
      this.session = storedSession;
    }
  };

  private readonly createNewSession = (): SessionInternal => {
    this.times = [];
    const session = {
      id: uuid(),
      startTime: new Date().toISOString(),
      lastActivityTime: Date.now(),
    };
    this.configStore.set(SESSION_KEY, session);
    this.session = session;
    this.callback.onNewSession(session);
    return session;
  };

  private readonly onBrowserActiveChange = (): void => {
    if (document.visibilityState === "visible") {
      this.onActivity();
    } else {
      this.onPageHidden();
    }
  };

  /**
   * The page was hidden (tab switch, minimise, close) or is being unloaded.
   * This can be the last chance to run, so stop counting, report the final
   * active time and let the owner flush it.
   */
  private readonly onPageHidden = (): void => {
    this.markAsIdle();
    this.notifyTheSession();
    this.callback.onPageHidden?.();
  };

  /**
   * Call this method to let listeners know that user is active.
   * Skipped when the active time has not changed since the last heartbeat,
   * e.g. when both `visibilitychange` and `pagehide` fire on close.
   */
  private readonly notifyTheSession = (): void => {
    if (this.callback.onContinueSession == null) return;
    const activeTime = Math.trunc(this.getTimeInMilliseconds());
    if (activeTime === this.lastNotifiedActiveTime) return;
    this.lastNotifiedActiveTime = activeTime;
    this.callback.onContinueSession(
      this.session,
      activeTime,
      Math.trunc(this.getSessionLengthInMilliseconds())
    );
  };

  private readonly markAsIdle = (): void => {
    this.isIdle = true;
    this.stopTimer();
  };

  /**
   * This method checks if user spent more than allowed idle time.
   * If yes, it marks the user as inactive. otherwise, calls heartbeat.
   * Uses Date.now() for cross-page consistency.
   */
  private readonly checkIfIdle = (): void => {
    this.syncSessionFromStore();
    const elapsed = Date.now() - this.session.lastActivityTime;
    if (elapsed > 0 && elapsed < this.idleTimeoutMs) {
      this.notifyTheSession();
    } else {
      this.markAsIdle();
    }
    this.configStore.set(SESSION_KEY, this.session); // store the session in memory.
  };

  private readonly onActivity = (): void => {
    const latestSession = this.getStoredSession();
    this.refreshSession(latestSession ?? this.session);

    if (this.isIdle) {
      this.startTimer();
      this.isIdle = false;
    }
    this.session.lastActivityTime = Date.now();
    this.configStore.set(SESSION_KEY, this.session);
  };

  private readonly registerEventListeners = (): void => {
    document.addEventListener("visibilitychange", this.onBrowserActiveChange);
    window.addEventListener("pagehide", this.onPageHidden);
    modifyEventListeners(
      "add",
      window,
      windowIdleEvents,
      this.onActivity,
      windowListenerOptions
    );
    modifyEventListeners(
      "add",
      document,
      documentIdleEvents,
      this.onActivity,
      documentListenerOptions
    );
  };

  private readonly unregisterEventListeners = (): void => {
    document.removeEventListener(
      "visibilitychange",
      this.onBrowserActiveChange
    );
    window.removeEventListener("pagehide", this.onPageHidden);
    modifyEventListeners(
      "remove",
      window,
      windowIdleEvents,
      this.onActivity,
      windowListenerOptions
    );
    modifyEventListeners(
      "remove",
      document,
      documentIdleEvents,
      this.onActivity,
      documentListenerOptions
    );
  };

  private readonly startTimer = (): void => {
    this.times.push({
      start: performance.now(),
      stop: null,
    });
    if (this.idleIntervalId == null) {
      this.idleIntervalId = window.setInterval(
        this.checkIfIdle,
        this.idleTimeCheckIntervalMs
      );
    }
  };

  private readonly stopTimer = (): void => {
    const current = this.times[this.times.length - 1];
    // Only close an open interval: re-stopping one that already ended would
    // count the idle gap since then as active time.
    if (current != null && current.stop == null) {
      current.stop = performance.now();
    }
    if (this.idleIntervalId != null) {
      window.clearInterval(this.idleIntervalId);
      this.idleIntervalId = undefined;
    }
  };

  private readonly getTimeInMilliseconds = (): number => {
    return this.times.reduce((acc, current) => {
      if (current.stop != null) {
        acc = acc + (current.stop - current.start);
      } else {
        acc = acc + (performance.now() - current.start);
      }
      return acc;
    }, 0);
  };

  private readonly getSessionLengthInMilliseconds = (): number => {
    const sessionStart = new Date(this.session.startTime).getTime();
    if (Number.isNaN(sessionStart)) {
      return 0;
    }
    return Math.max(0, Date.now() - sessionStart);
  };

  public destroy = (): void => {
    // flush the data before page unloads.
    this.stopTimer();
    this.notifyTheSession();
    // Persist final session state for cross-page continuity
    this.configStore.set(SESSION_KEY, this.session);
    this.times = [];
    this.lastNotifiedActiveTime = -1;
    this.isIdle = true;
    this.unregisterEventListeners();
  };
}
